import clsx from 'clsx';
import * as React from 'react';
import { usePress } from 'react-aria';

import { FRAGMENT_ANNOTATION_BODY } from '@gitbook/doc-core';
import { PortalContext, PortalRelative } from '@gitbook/react-uikit';
import { isTextRangeSelection, TextRange } from '@gitbook/slate';
import type { InlineProps } from '@gitbook/slate-react';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { AnnotationPopover } from './AnnotationPopover';

/**
 * Component to render an inline annotation node.
 */
export function InlineAnnotation(props: InlineProps) {
    const { editor, node, children, attributes, isFocused } = props;

    const { readOnly } = editor;

    const [opened, setOpened] = React.useState(false);

    const focusFragment = useEventCallback(() => {
        if (readOnly) {
            return;
        }
        editor.change((change) => {
            const fragment = node.getFragmentByKey(FRAGMENT_ANNOTATION_BODY);
            if (fragment) {
                if (!isTextRangeSelection(change.value.selection)) {
                    change
                        .selectText(
                            TextRange.fromBlockRange(change.value.selection, change.value.document)
                        )
                        .collapseToEndOf(fragment)
                        .focus();
                } else {
                    change.collapseToEndOf(fragment).focus();
                }
            }
        });
    });

    const unfocusFragment = useEventCallback(() => {
        if (readOnly) {
            return;
        }
        editor.change((change) => {
            const inline = change.value.document.getDescendant(node.key);
            if (inline?.isInline()) {
                const fragment = inline.getFragmentByKey(FRAGMENT_ANNOTATION_BODY);
                if (
                    fragment &&
                    isTextRangeSelection(change.value.selection) &&
                    change.value.selection.hasEdgeIn(fragment)
                ) {
                    change.collapseToEndOf(inline);
                }
            }
        });
    });

    const openIfBodyFocused = useEventCallback(() => {
        const { selection } = editor.value;

        const inline = editor.value.document.getNode(node.key);
        if (!inline || !inline.isInline()) {
            return;
        }
        const bodyFragment = inline.getFragmentByKey(FRAGMENT_ANNOTATION_BODY);
        if (!bodyFragment) {
            return;
        }

        const hasSelectionInBody =
            isTextRangeSelection(selection) && !!selection.hasAnchorIn(bodyFragment);
        if (hasSelectionInBody) {
            setOpened(true);
        }
    });

    // When the body is focused, we opened the popover
    // This is mainly needed when creating a popover where the creation action will only insert the node and focus its body
    // without being able to directly change the state here.
    React.useEffect(() => {
        if (!isFocused || readOnly) {
            return;
        }

        openIfBodyFocused();
    }, [node.key, isFocused, readOnly, openIfBodyFocused]);

    // Synchronize the editor selection when opening/closing the popover
    React.useEffect(() => {
        if (readOnly) {
            return;
        }

        if (opened) {
            // When opening, we want to focus the fragment in the popover
            focusFragment();
        } else {
            // When closing, if the focus was in the fragment, we move it outside.
            unfocusFragment();
        }
    }, [opened, readOnly, focusFragment, unfocusFragment]);

    const onPress = React.useCallback(() => {
        setOpened(true);
    }, []);

    const onClose = React.useCallback(() => {
        setOpened(false);
    }, []);

    const { pressProps } = usePress({ onPress });

    return (
        <PortalContext.Provider value={onClose}>
            <PortalRelative
                closeOnClick={false}
                closeOnEsc
                closeOnScroll={false}
                closeOnClickInNestedPortals={false}
                closeOnOutsideClick
                opened={opened}
                anchor={({ ref }) => (
                    <span
                        // @ts-expect-error
                        ref={ref}
                        className={clsx(
                            '-mx-1 cursor-pointer rounded px-1 text-content-paragraph underline decoration-2 decoration-muted decoration-dotted',
                            'hover:-z-[1] hover:bg-[rgba(35,131,226,0.14)]',
                            opened && '-z-[1] bg-[rgba(35,131,226,0.14)]'
                        )}
                        {...attributes}
                        {...pressProps}
                    >
                        {children}
                    </span>
                )}
            >
                <AnnotationPopover node={node} editor={editor} onClose={onClose} />
            </PortalRelative>
        </PortalContext.Provider>
    );
}
